import React, { useState, useEffect } from "react";
import RBMEvaluator from "./RBMEvaluator";

const Content = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [videos, setVideos] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);

  const BASE_URL = "https://schoolofservice.co.za";
  const IMAGE_PATH = `${BASE_URL}/backend/uploads`;

  // Fetch categories
  useEffect(() => {
    fetch(`${BASE_URL}/backend/get-categories.php`)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success" && Array.isArray(data.categories)) {
          setCategories(data.categories);
        } else {
          setCategories([]);
          console.error("Unexpected categories response:", data);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch categories:", error);
        setCategories([]);
      });
  }, []);

  // Fetch courses
  useEffect(() => {
    fetch(`${BASE_URL}/backend/get-courses.php`)
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setCourses(data);
          setFilteredCourses(data);
          if (data.length > 0) setSelectedCourse(data[0].coursesID);
        } else {
          console.error("Courses API did not return an array:", data);
        }
      })
      .catch((error) => console.error("Failed to fetch courses:", error));
  }, []);

  // Filter courses by selected category
  useEffect(() => {
    if (selectedCategory) {
      const filtered = courses.filter((course) => course.categoryID === selectedCategory);
      setFilteredCourses(filtered);
      setSelectedCourse(filtered.length ? filtered[0].coursesID : null);
    } else {
      setFilteredCourses(courses);
    }
  }, [selectedCategory, courses]);

  // Fetch videos and PDFs
  useEffect(() => {
    if (selectedCourse) {
      setLoading(true);
      Promise.all([
        fetch(`${BASE_URL}/backend/get-demo.php?courseId=${selectedCourse}`)
          .then((res) => res.json())
          .then((videoData) => {
            if (videoData.status === "success") {
              console.log("Videos from backend:", videoData.data);
              setVideos(videoData.data);
            } else {
              setVideos([]);
              console.error("Unexpected videos response format:", videoData);
            }
          }),
        fetch(`${BASE_URL}/backend/get-pdfs.php?courseId=${selectedCourse}`)
          .then((res) => res.json())
          .then((pdfData) => {
            if (Array.isArray(pdfData)) {
              setPdfs(pdfData);
            } else if (pdfData.status === "success" && Array.isArray(pdfData.data)) {
              setPdfs(pdfData.data);
            } else {
              setPdfs([]);
              console.error("Unexpected PDFs response format:", pdfData);
            }
          }),
      ])
        .catch((error) => console.error("Error fetching videos or PDFs:", error))
        .finally(() => setLoading(false));
    }
  }, [selectedCourse]);

  return (
    <main className="container py-5 text-center">
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <a className="nav-link active" id="courses-tab" data-bs-toggle="tab" href="#courses" role="tab">
            Courses
          </a>
        </li>
        <li className="nav-item">
          <a className="nav-link" id="rbm-evaluator-tab" data-bs-toggle="tab" href="#rbm-evaluator" role="tab">
            RBM Evaluator
          </a>
        </li>
      </ul>

      <div className="tab-content">
        <div className="tab-pane fade show active" id="courses" role="tabpanel">
          <div className="d-flex flex-wrap">
            <button
              className={`btn btn-outline-secondary m-2 ${!selectedCategory ? "active" : ""}`}
              onClick={() => setSelectedCategory(null)}
            >
              All Categories
            </button>
            {categories.map((category) => (
              <button
                key={category.categoryID}
                className={`btn btn-outline-secondary m-2 ${
                  selectedCategory === category.categoryID ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category.categoryID)}
              >
                {category.categoryName}
              </button>
            ))}
          </div>

          <div className="d-flex flex-wrap">
            {filteredCourses.map((course) => (
              <button
                key={course.coursesID}
                className={`btn btn-outline-primary m-2 ${
                  selectedCourse === course.coursesID ? "active" : ""
                }`}
                onClick={() => setSelectedCourse(course.coursesID)}
              >
                {course.courseName}
              </button>
            ))}
          </div>

          {isLoading && <p>Loading...</p>}

          {!isLoading && selectedCourse && (
            <div>
              {videos.length > 0 ? (
                <div className="d-flex flex-wrap">
                  {videos.map((video) => {
                    const imageUrl = `${IMAGE_PATH}/${video.image_url.split("/").pop()}`;
                    console.log("Constructed Image URL:", imageUrl);
                    return (
                      <div className="card m-2" style={{ width: "18rem" }} key={video.id}>
                        {playingVideo === video.id ? (
                          <iframe
                            className="card-img-top"
                            src={video.video_url}
                            title={video.video_name}
                            style={{ width: "100%", height: "200px" }}
                            frameBorder="0"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                          ></iframe>
                        ) : (
                          <img
                            src={imageUrl}
                            alt={video.video_name}
                            className="card-img-top"
                            style={{ width: "100%", height: "200px", cursor: "pointer" }}
                            onClick={() => setPlayingVideo(video.id)}
                            onError={(e) => console.error(`Failed to load image: ${imageUrl}`)}
                          />
                        )}
                        <div className="card-body">
                          <h5 className="card-title">{video.video_name}</h5>
                          <p className="card-text">
                            <strong>Lecture:</strong> {video.lecture_name} <br />
                            <strong>Duration:</strong> {video.duration} <br />
                            <strong>Upload Time:</strong> {video.upload_time}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>No videos available for this course.</p>
              )}

              <div className="d-flex flex-wrap">
                {pdfs.length > 0 ? (
                  pdfs.map((pdf) => (
                    <div key={pdf.pdfId} className="card m-2" style={{ width: "18rem" }}>
                      <div className="card-body">
                        <h5 className="card-title">{pdf.fileName}</h5>
                        <button
                          onClick={() =>
                            window.open(`${BASE_URL}/uploads/pdfs/${pdf.fileName}`, "_blank")
                          }
                          className="btn btn-primary"
                        >
                          View PDF
                        </button>
                        <a
                          href={`${BASE_URL}/uploads/pdfs/${pdf.fileName}`}
                          download
                          className="btn btn-secondary ms-2"
                        >
                          Download PDF
                        </a>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No PDFs available for this course.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="tab-pane fade" id="rbm-evaluator" role="tabpanel">
          <RBMEvaluator />
        </div>
      </div>
    </main>
  );
};

export default Content;